import { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Menu } from 'lucide-react'
import { cn } from '@/lib/utils'

const LANGUAGES = [
    { code: 'cs', label: 'CS' },
    { code: 'pl', label: 'PL' },
    { code: 'en', label: 'EN' },
]

export const LanguageSwitcher = () => {
    const { i18n } = useTranslation()
    const [open, setOpen] = useState(false)
    const wrapRef = useRef<HTMLDivElement>(null)

    const change = (code: string) => {
        void i18n.changeLanguage(code)
        localStorage.setItem('lang', code)
        setOpen(false)
    }

    useEffect(() => {
        if (!open) return
        const onClick = (e: MouseEvent) => {
            if (wrapRef.current && !wrapRef.current.contains(e.target as Node)) {
                setOpen(false)
            }
        }
        document.addEventListener('mousedown', onClick)
        return () => document.removeEventListener('mousedown', onClick)
    }, [open])

    const buttonClass = (code: string) =>
        cn(
            'text-sm font-black px-3 py-1.5 rounded-lg transition-colors',
            i18n.language === code
                ? 'bg-brand text-white'
                : 'text-slate-500 hover:text-slate-700 hover:bg-slate-100',
        )

    return (
        <>
            {/* Desktop: inline buttons */}
            <div className="hidden sm:flex items-center gap-1.5">
                {LANGUAGES.map(({ code, label }) => (
                    <button key={code} onClick={() => change(code)} className={buttonClass(code)}>
                        {label}
                    </button>
                ))}
            </div>

            {/* Mobile: hamburger + dropdown */}
            <div className="sm:hidden relative" ref={wrapRef}>
                <button
                    onClick={() => setOpen((v) => !v)}
                    className="p-2 rounded-lg text-slate-600 hover:bg-slate-100 transition-colors"
                    aria-label="Menu"
                >
                    <Menu className="size-5" />
                </button>
                {open && (
                    <div className="absolute right-0 top-full mt-2 bg-white border border-slate-200 rounded-2xl shadow-xl p-2 z-50 min-w-[140px] flex flex-col gap-1">
                        {LANGUAGES.map(({ code, label }) => (
                            <button
                                key={code}
                                onClick={() => change(code)}
                                className={cn(
                                    'text-sm font-black px-3 py-2 rounded-lg text-left transition-colors',
                                    i18n.language === code
                                        ? 'bg-brand text-white'
                                        : 'text-slate-600 hover:bg-slate-100',
                                )}
                            >
                                {label}
                            </button>
                        ))}
                    </div>
                )}
            </div>
        </>
    )
}
